import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, Users, MapPin, Phone, ChevronLeft, 
  Loader2, CalendarCheck, AlertCircle, XCircle, CheckCircle2,
  MoreVertical, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';

const statusConfig = {
  pending: { 
    label: 'Pending', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock 
  },
  confirmed: { 
    label: 'Confirmed', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle2 
  },
  seated: { 
    label: 'Seated', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Users 
  },
  completed: { 
    label: 'Completed', 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: CalendarCheck 
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: XCircle 
  },
  no_show: { 
    label: 'No Show', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: AlertCircle 
  }
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Reservation Card Component
function ReservationCard({ reservation, onCancel }) {
  const status = statusConfig[reservation.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  
  const isUpcoming = () => {
    const resDate = new Date(reservation.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return resDate >= today && !['cancelled', 'completed', 'no_show'].includes(reservation.status);
  };
  
  const canCancel = ['pending', 'confirmed'].includes(reservation.status) && isUpcoming();

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Left side - Date badge */}
          <div className="bg-primary/5 p-4 md:p-6 flex flex-row md:flex-col items-center justify-center gap-2 md:gap-1 md:min-w-[100px]">
            <Calendar className="h-5 w-5 text-primary" />
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">
                {new Date(reservation.date).getDate()}
              </p>
              <p className="text-sm text-muted-foreground">
                {new Date(reservation.date).toLocaleDateString('en-US', { month: 'short' })}
              </p>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg">{reservation.restaurant?.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {reservation.restaurant?.address}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-xs", status.color)}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {status.label}
                </Badge>
                {canCancel && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => onCancel(reservation)}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Reservation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{formatTime(reservation.time)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{reservation.party_size} {reservation.party_size === 1 ? 'guest' : 'guests'}</span>
              </div>
              {reservation.table_number && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Table:</span>
                  <span>{reservation.table_number}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Confirmation: <span className="font-mono font-medium">{reservation.confirmation_code}</span>
              </p>
              {reservation.restaurant?.phone && (
                <a 
                  href={`tel:${reservation.restaurant.phone}`}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Phone className="h-3 w-3" />
                  Call Restaurant
                </a>
              )}
            </div>

            {reservation.special_requests && (
              <div className="bg-muted/50 rounded-lg p-2 text-sm">
                <span className="text-muted-foreground">Special Requests: </span>
                {reservation.special_requests}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Empty State Component
function EmptyState({ type, onBrowse }) {
  return (
    <div className="text-center py-12">
      <CalendarCheck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
      <h3 className="font-medium text-lg mb-1">
        {type === 'upcoming' ? 'No Upcoming Reservations' : 'No Past Reservations'}
      </h3>
      <p className="text-muted-foreground text-sm mb-4">
        {type === 'upcoming' 
          ? 'Book a table at your favorite restaurant!'
          : 'Your past reservations will appear here.'}
      </p>
      {type === 'upcoming' && (
        <Button onClick={onBrowse}>
          Browse Restaurants
        </Button>
      )}
    </div>
  );
}

export default function MyReservationsPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reservations/my-reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReservations(response.data.reservations || []);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      toast.error('Failed to load reservations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    fetchReservations();
  }, [token, navigate, fetchReservations]);

  const handleCancelReservation = async () => {
    if (!selectedReservation) return;
    
    setCancelling(true);
    try {
      await axios.patch(
        `${API}/reservations/${selectedReservation.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reservation cancelled');
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel reservation');
    } finally {
      setCancelling(false);
      setCancelDialogOpen(false);
      setSelectedReservation(null);
    }
  };

  const openCancelDialog = (reservation) => {
    setSelectedReservation(reservation);
    setCancelDialogOpen(true);
  };

  const isUpcoming = (reservation) => {
    const resDate = new Date(reservation.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return resDate >= today && !['cancelled', 'completed', 'no_show'].includes(reservation.status);
  };

  const isPast = (reservation) => {
    return ['cancelled', 'completed', 'no_show'].includes(reservation.status) || 
           new Date(reservation.date) < new Date().setHours(0, 0, 0, 0);
  };

  const upcomingReservations = reservations.filter(isUpcoming);
  const pastReservations = reservations.filter(isPast);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="my-reservations-page">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">My Reservations</h1>
              <p className="text-muted-foreground text-sm">Manage your table reservations</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReservations}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="upcoming" className="space-y-4">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="upcoming" className="gap-2">
              <Calendar className="h-4 w-4" />
              Upcoming ({upcomingReservations.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="gap-2">
              <CalendarCheck className="h-4 w-4" />
              Past ({pastReservations.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-4">
            {upcomingReservations.length === 0 ? (
              <EmptyState type="upcoming" onBrowse={() => navigate('/')} />
            ) : (
              upcomingReservations.map((reservation) => (
                <ReservationCard 
                  key={reservation.id} 
                  reservation={reservation} 
                  onCancel={openCancelDialog}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="space-y-4">
            {pastReservations.length === 0 ? (
              <EmptyState type="past" onBrowse={() => navigate('/')} />
            ) : (
              pastReservations.map((reservation) => (
                <ReservationCard 
                  key={reservation.id} 
                  reservation={reservation} 
                  onCancel={openCancelDialog}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Reservation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your reservation at{' '}
              <strong>{selectedReservation?.restaurant?.name}</strong> on{' '}
              <strong>{selectedReservation && formatDate(selectedReservation.date)}</strong> at{' '}
              <strong>{selectedReservation && formatTime(selectedReservation.time)}</strong>?
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Reservation</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelReservation}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Cancel Reservation'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
